import Button from '@/components/Buttons/Button';
import { toast } from '@/components/toast';
import { updatePresenterIdleVideo } from '@/services/api/did-api/backoffice-api';
import { uploadFile } from '@/services/api/studioApi';
import { useAuth } from '@/services/auth';
import { Box, FormControl, InputLabel, MenuItem, Paper, Select, styled, TextField, Typography } from '@mui/material';
import { useState } from 'react';

interface PresenterIdleVideoFormProps {
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    fontSize: '1.5rem',
}));

export const PresenterIdleVideoForm = ({ onSuccess, onError }: PresenterIdleVideoFormProps) => {
    const { user } = useAuth();
    const [presenterId, setPresenterId] = useState('');
    const [currentOwnerId, setCurrentOwnerId] = useState('');
    const [useMe, setUseMe] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const isV2Presenter = presenterId.startsWith('v2_');
    const isFormValid = isV2Presenter && presenterId && (useMe || currentOwnerId) && selectedFile;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile) return;

        setIsSubmitting(true);

        try {
            const sourceUrl = await uploadFile(selectedFile);
            await updatePresenterIdleVideo(presenterId, {
                ownerId: useMe ? user?.id : currentOwnerId,
                idle_video: sourceUrl,
            });

            toast.success({ title: 'Presenter idle video updated successfully' });
            onSuccess?.();

            setPresenterId('');
            setCurrentOwnerId('');
            setUseMe(true);
            setSelectedFile(null);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || 'Failed to update presenter idle video';
            toast.error({ title: errorMessage });
            onError?.(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Update Presenter Idle Video
                </Typography>
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="Presenter ID"
                        value={presenterId}
                        onChange={e => setPresenterId(e.target.value)}
                        required
                        fullWidth
                        error={presenterId !== '' && !isV2Presenter}
                        helperText={presenterId !== '' && !isV2Presenter ? 'Only v2 presenters are supported' : ''}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Current Owner</InputLabel>
                        <Select
                            value={useMe ? 'me' : 'other'}
                            onChange={e => setUseMe(e.target.value === 'me')}
                            label="Current Owner"
                            defaultValue="me">
                            <MenuItem value="me">Me</MenuItem>
                            <MenuItem value="other">Other User</MenuItem>
                        </Select>
                    </FormControl>

                    {!useMe && (
                        <TextField
                            label="Current Owner ID"
                            value={currentOwnerId}
                            onChange={e => setCurrentOwnerId(e.target.value)}
                            required
                            fullWidth
                        />
                    )}

                    <TextField
                        type="file"
                        inputProps={{
                            accept: 'video/*',
                        }}
                        onChange={handleFileChange}
                        required
                        fullWidth
                    />

                    <StyledButton type="submit" variant="contained" disabled={isSubmitting || !isFormValid}>
                        {isSubmitting ? 'Updating...' : 'Update Idle Video'}
                    </StyledButton>
                </Box>
            </Paper>
        </Box>
    );
};
